import { useState } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Video, Users, Calendar, Copy, ExternalLink, Clock, CheckCircle } from "lucide-react";
import { generateJitsiMeetingUrl, generateJitsiRoomName } from "@/utils/jitsi";
import MeetingRoom from "@/components/meetings/MeetingRoom";
import { toast } from "sonner";

const translations = {
  en: {
    title: "Video Conference",
    subtitle: "Start or join video meetings with clients",
    startMeeting: "Start New Meeting",
    joinMeeting: "Join Meeting",
    scheduleMeeting: "Schedule Meeting",
    instantMeeting: "Instant Meeting",
    instantDesc: "Start a meeting right now and invite participants",
    scheduledMeeting: "Scheduled Meeting",
    scheduledDesc: "Plan a meeting for a specific date and time",
    joinExisting: "Join Existing",
    joinDesc: "Enter a meeting code to join",
    meetingTitle: "Meeting Title",
    meetingTitlePlaceholder: "e.g., Client Consultation",
    roomCode: "Room Code or URL",
    roomCodePlaceholder: "Enter meeting code or URL",
    start: "Start Meeting",
    join: "Join",
    copyLink: "Copy Link",
    linkCopied: "Meeting link copied!",
    openInNewTab: "Open in New Tab",
    leaveMeeting: "Leave Meeting",
    activeMeeting: "Active Meeting",
    inProgress: "In Progress",
    endMeeting: "End Meeting",
    noActiveMeeting: "No active meeting",
    createOrJoin: "Start or join a meeting to begin",
    recentMeetings: "Recent Meetings",
    noRecent: "No recent meetings",
    meetingFeatures: "Meeting Features",
    screenShare: "Screen Sharing",
    recording: "Meeting Recording",
    chat: "In-Meeting Chat",
    participants: "Up to 50 Participants",
  },
  fr: {
    title: "Visioconférence",
    subtitle: "Démarrez ou rejoignez des réunions vidéo avec vos clients",
    startMeeting: "Démarrer Réunion",
    joinMeeting: "Rejoindre Réunion",
    scheduleMeeting: "Planifier Réunion",
    instantMeeting: "Réunion Instantanée",
    instantDesc: "Démarrez une réunion maintenant et invitez des participants",
    scheduledMeeting: "Réunion Planifiée",
    scheduledDesc: "Planifiez une réunion pour une date et heure spécifiques",
    joinExisting: "Rejoindre Existante",
    joinDesc: "Entrez un code de réunion pour rejoindre",
    meetingTitle: "Titre de la Réunion",
    meetingTitlePlaceholder: "ex., Consultation Client",
    roomCode: "Code ou URL de Réunion",
    roomCodePlaceholder: "Entrez le code ou URL",
    start: "Démarrer",
    join: "Rejoindre",
    copyLink: "Copier le Lien",
    linkCopied: "Lien de réunion copié!",
    openInNewTab: "Ouvrir dans Nouvel Onglet",
    leaveMeeting: "Quitter Réunion",
    activeMeeting: "Réunion Active",
    inProgress: "En Cours",
    endMeeting: "Terminer Réunion",
    noActiveMeeting: "Aucune réunion active",
    createOrJoin: "Démarrez ou rejoignez une réunion",
    recentMeetings: "Réunions Récentes",
    noRecent: "Aucune réunion récente",
    meetingFeatures: "Fonctionnalités",
    screenShare: "Partage d'Écran",
    recording: "Enregistrement",
    chat: "Chat en Réunion",
    participants: "Jusqu'à 50 Participants",
  },
};

const partnerRoleMap: Record<string, { en: string; fr: string }> = {
  "business-law": { en: "Business Lawyer", fr: "Avocat d'Affaires" },
  "immigration-law": { en: "Immigration Lawyer", fr: "Avocat en Immigration" },
  "notary": { en: "Notary", fr: "Notaire" },
  "tax-advisor": { en: "Tax Advisor", fr: "Conseiller Fiscal" },
  "cpa": { en: "CPA", fr: "CPA" },
};

export default function PartnerVideoConference() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [isInMeeting, setIsInMeeting] = useState(false);
  const [currentRoomName, setCurrentRoomName] = useState<string>("");
  const [meetingTitle, setMeetingTitle] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [generatedMeetingUrl, setGeneratedMeetingUrl] = useState<string>("");

  const userRole = partnerType ? partnerRoleMap[partnerType]?.[language as 'en' | 'fr'] || partnerType : "Partner";
  const userName = user?.displayName || user?.email?.split('@')[0] || "Partner";

  const handleStartMeeting = () => {
    const title = meetingTitle || `${userRole} Meeting`;
    const roomName = generateJitsiRoomName(title);
    const meetingUrl = generateJitsiMeetingUrl(roomName);
    
    setCurrentRoomName(roomName);
    setGeneratedMeetingUrl(meetingUrl);
    setIsInMeeting(true);
    setShowStartDialog(false);
    setMeetingTitle("");
  };

  const handleJoinMeeting = () => {
    if (!joinCode.trim()) {
      toast.error(language === 'fr' ? 'Entrez un code de réunion' : 'Enter a meeting code');
      return;
    }

    // Extract room name from URL or use as-is
    let roomName = joinCode.trim();
    if (roomName.includes('meet.jit.si/')) {
      roomName = roomName.split('meet.jit.si/').pop() || roomName;
    }

    setCurrentRoomName(roomName);
    setGeneratedMeetingUrl(generateJitsiMeetingUrl(roomName));
    setIsInMeeting(true);
    setShowJoinDialog(false);
    setJoinCode("");
  };

  const handleLeaveMeeting = () => {
    setIsInMeeting(false);
    setCurrentRoomName("");
    setGeneratedMeetingUrl("");
    toast.success(language === 'fr' ? 'Réunion terminée' : 'Meeting ended');
  };

  const copyMeetingLink = () => {
    if (generatedMeetingUrl) {
      navigator.clipboard.writeText(generatedMeetingUrl);
      toast.success(t.linkCopied);
    }
  };

  const openInNewTab = () => {
    if (generatedMeetingUrl) {
      window.open(generatedMeetingUrl, '_blank');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
        <p className="text-muted-foreground mt-2">{t.subtitle}</p>
      </div>

      {isInMeeting ? (
        <div className="space-y-4">
          {/* Active Meeting Header */}
          <Card className="border-green-500/50 bg-green-50/50 dark:bg-green-950/20">
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-full animate-pulse">
                  <Video className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{t.activeMeeting}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {t.inProgress}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyMeetingLink}>
                  <Copy className="h-4 w-4 mr-2" />
                  {t.copyLink}
                </Button>
                <Button variant="outline" size="sm" onClick={openInNewTab}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t.openInNewTab}
                </Button>
                <Button variant="destructive" size="sm" onClick={handleLeaveMeeting}>
                  {t.endMeeting}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Jitsi Meeting Room */}
          <MeetingRoom
            roomName={currentRoomName}
            userName={userName}
            userRole={userRole}
            onLeave={handleLeaveMeeting}
          />
        </div>
      ) : (
        <>
          {/* Meeting Options */}
          <div className="grid gap-6 md:grid-cols-3">
            {/* Start Instant Meeting */}
            <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
              <DialogTrigger asChild>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary">
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <div className="p-4 bg-primary/10 rounded-full mb-4">
                      <Video className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold">{t.instantMeeting}</h3>
                    <p className="text-sm text-muted-foreground text-center mt-2">{t.instantDesc}</p>
                    <Button className="mt-4 w-full">{t.startMeeting}</Button>
                  </CardContent>
                </Card>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t.startMeeting}</DialogTitle>
                  <DialogDescription>{t.instantDesc}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="meeting-title">{t.meetingTitle}</Label>
                    <Input
                      id="meeting-title"
                      placeholder={t.meetingTitlePlaceholder}
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                    />
                  </div>
                  <Button className="w-full" onClick={handleStartMeeting}>
                    <Video className="h-4 w-4 mr-2" />
                    {t.start}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Schedule Meeting */}
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <div className="p-4 bg-blue-500/10 rounded-full mb-4">
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold">{t.scheduledMeeting}</h3>
                <p className="text-sm text-muted-foreground text-center mt-2">{t.scheduledDesc}</p>
                <Button variant="outline" className="mt-4 w-full">{t.scheduleMeeting}</Button>
              </CardContent>
            </Card>

            {/* Join Existing */}
            <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
              <DialogTrigger asChild>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary">
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <div className="p-4 bg-green-500/10 rounded-full mb-4">
                      <Users className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold">{t.joinExisting}</h3>
                    <p className="text-sm text-muted-foreground text-center mt-2">{t.joinDesc}</p>
                    <Button variant="outline" className="mt-4 w-full">{t.joinMeeting}</Button>
                  </CardContent>
                </Card>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t.joinMeeting}</DialogTitle>
                  <DialogDescription>{t.joinDesc}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="room-code">{t.roomCode}</Label>
                    <Input
                      id="room-code"
                      placeholder={t.roomCodePlaceholder}
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleJoinMeeting()}
                    />
                  </div>
                  <Button className="w-full" onClick={handleJoinMeeting}>
                    <Users className="h-4 w-4 mr-2" />
                    {t.join}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Features */}
          <Card>
            <CardHeader>
              <CardTitle>{t.meetingFeatures}</CardTitle>
              <CardDescription>
                {language === 'fr' 
                  ? 'Fonctionnalités disponibles pendant vos réunions' 
                  : 'Features available during your meetings'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: t.screenShare, color: "text-blue-500 bg-blue-500/10" },
                  { label: t.recording, color: "text-red-500 bg-red-500/10" },
                  { label: t.chat, color: "text-green-500 bg-green-500/10" },
                  { label: t.participants, color: "text-purple-500 bg-purple-500/10" },
                ].map((feature, idx) => (
                  <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${feature.color.split(' ')[1]}`}>
                    <CheckCircle className={`h-5 w-5 ${feature.color.split(' ')[0]}`} />
                    <span className="text-sm font-medium">{feature.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}